/* eslint-disable import/no-named-as-default */
import React, { Component, ReactNode } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Timeline from "../../Charts/Timeline";
import TimeScaleDropDown from "./TimeScaleDropDown";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";
import { ChartTitle } from "../styles/employer-analytics-styles";
import { Wrapper, Chart } from "../styles/time-to-hire-timeline-styles";

interface ITimeToHireTimelineProps {
  timeToHireTimeline?: {
    months?: string[];
    yourCompany?: number[];
    hiringHub?: number[];
    industryAvg?: number[];
  };
  pdfExport?: boolean;
  totalJobsPerSectorSelected: boolean;
  timeToHireTimelineWidgetLoader: boolean;
}

interface ITimeToHireTimelineState {
  timescaleDropdownActive: boolean;
  activeTimescale: {
    id: number;
    name: string;
  };
}

export class TimeToHireTimeline extends Component<
  ITimeToHireTimelineProps,
  ITimeToHireTimelineState
> {
  static propTypes = {
    timeToHireTimeline: PropTypes.shape({
      months: PropTypes.arrayOf(PropTypes.string),
      yourCompany: PropTypes.arrayOf(PropTypes.number),
      hiringHub: PropTypes.arrayOf(PropTypes.number),
      industryAvg: PropTypes.arrayOf(PropTypes.number),
    }),
    timeToHireTimelineWidgetLoader: PropTypes.bool.isRequired,
    totalJobsPerSectorSelected: PropTypes.bool,
    pdfExport: PropTypes.bool,
  };

  static defaultProps = {
    pdfExport: null,
    totalJobsPerSectorSelected: null,
    timeToHireTimeline: {
      months: [],
      yourCompany: [],
      hiringHub: [],
      industryAvg: [],
    },
  };

  state = {
    timescaleDropdownActive: false,
    activeTimescale: {
      id: 0,
      name: "12 month view",
    },
  };

  toggleDropdown = (): void => {
    this.setState(({ timescaleDropdownActive }) => ({
      timescaleDropdownActive: !timescaleDropdownActive,
    }));
  };

  selectTimescale = (timescale: { id: number; name: string }): void => {
    this.setState({
      activeTimescale: timescale,
    });
  };

  render(): ReactNode {
    const {
      timeToHireTimeline,
      timeToHireTimelineWidgetLoader,
      pdfExport,
      totalJobsPerSectorSelected,
    } = this.props;
    const { timescaleDropdownActive, activeTimescale } = this.state;

    const timeToHireTimelineOptions = {
      series: [
        {
          name: "Your Company",
          data: timeToHireTimeline.yourCompany,
        },
        {
          name: "Hiring Hub",
          data: timeToHireTimeline.hiringHub,
        },
        {
          name: "Industry Average",
          data: timeToHireTimeline.industryAvg,
        },
      ],
      options: {
        height: 290,
        colors: ["rgb(0, 143, 251)", "#f2008c", "rgb(0, 227, 150)"],
        chart: {
          type: "line",
          offsetY: -5,
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          width: [5, 2, 2],
          curve: "straight",
          dashArray: [0, 4, 0],
        },
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"],
            opacity: 0.3,
          },
        },
        tooltip: {
          y: [
            {
              title: {
                formatter: (val: string) => val,
              },
            },
            {
              title: {
                formatter: (val: string) => val,
              },
            },
            {
              title: {
                formatter: (val: string) => val,
              },
            },
          ],
        },
        xaxis: {
          categories: timeToHireTimeline.months,
        },
        legend: {
          offsetY: 5,
          tooltipHoverFormatter: (
            val: string,
            opts: {
              // I've typed the opts manually because ApexCharts seem to have
              // typed this as 'any' which our lint config doesn't like
              // (at the time of writing, anyway) see
              // https://github.com/apexcharts/apexcharts.js/blob/master/types/apexcharts.d.ts#L744
              w: {
                globals: { series: number[][] };
              };
              seriesIndex: number;
              dataPointIndex: number;
            }
          ) => {
            const value = `${val} - ${
              opts.w.globals.series[opts.seriesIndex][opts.dataPointIndex]
            } `;
            return value;
          },
        },
      },
    };

    const timescaleOptions = [
      { id: 0, name: "12 month view" },
      { id: 30, name: "Last 30 days" },
      { id: 90, name: "Last 90 days" },
    ];

    return (
      <Wrapper
        className="animated fadeIn delay-8"
        pdfExport={pdfExport}
        totalJobsPerSectorSelected={totalJobsPerSectorSelected}
      >
        <AnalyticsWidgetLoading
          text="Updating..."
          active={timeToHireTimelineWidgetLoader}
        />
        <ChartTitle data-test-id="time-to-hire-timeline-title" marginBottom={5}>
          Time to Hire
          {!pdfExport && (
            <TimeScaleDropDown
              toggleDropdown={this.toggleDropdown}
              dropdownActive={timescaleDropdownActive}
              selectTimescale={this.selectTimescale}
              activeTimescale={activeTimescale}
              options={timescaleOptions}
              type="TTH_TIMELINE"
              testId="tth-timeline-chart-timescale-dropdown"
            />
          )}
        </ChartTitle>
        <Chart>
          <Timeline
            graphOptions={timeToHireTimelineOptions}
            dataTestId="time-to-hire-timeline"
          />
        </Chart>
      </Wrapper>
    );
  }
}

interface IReduxState {
  employerAnalytics: {
    chartsData: {
      timeToHireTimeline: {
        months: string[];
        yourCompany: number[];
        hiringHub: number[];
        industryAvg: number[];
      };
    };
    timeToHireTimelineWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  timeToHireTimeline: state.employerAnalytics.chartsData.timeToHireTimeline,
  timeToHireTimelineWidgetLoader:
    state.employerAnalytics.timeToHireTimelineWidgetLoader,
});

export default connect(mapStateToProps, {})(TimeToHireTimeline);
